import { useCallback, useRef, useState } from 'react';
import {
  MAX_CONCURRENT_UPLOADS,
  canonicalVideoMime,
  type AcceptedVideoMimeType,
  type CompleteUploadInput,
  type InitiateUploadInput,
  type InitiateUploadResponse,
} from '@ddp/shared';

import { apiFetch } from '../../api/client.js';
import { sizeBucket, track } from '../analytics/analytics.js';

/**
 * Upload multipart résumable (PRD FR-6 à FR-11).
 *
 * Les parts partent **directement du navigateur vers le stockage objet** via des URLs
 * pré-signées : l'API ne voit passer aucun octet vidéo (docs/architecture.md §4).
 *
 * Deux niveaux de reprise :
 *  - dans la session, une part qui échoue est réessayée avec backoff ;
 *  - après rechargement de page, `resume()` interroge le serveur, qui interroge le stockage,
 *    pour savoir quelles parts sont déjà arrivées. Le stockage est la source de vérité —
 *    le client, lui, a tout oublié.
 */

export type UploadState = 'idle' | 'preparing' | 'uploading' | 'completing' | 'done' | 'error';

export interface UploadProgress {
  state: UploadState;
  /** Progression dans `[0,1]`. */
  progress: number;
  uploadedBytes: number;
  totalBytes: number;
  error: string | null;
  videoId: string | null;
}

const MAX_PART_RETRIES = 3;

export function useMultipartUpload() {
  const [progress, setProgress] = useState<UploadProgress>({
    state: 'idle',
    progress: 0,
    uploadedBytes: 0,
    totalBytes: 0,
    error: null,
    videoId: null,
  });

  const abortRef = useRef<AbortController | null>(null);

  const upload = useCallback(async (file: File, fieldSessionId: string): Promise<string | null> => {
    const controller = new AbortController();
    abortRef.current = controller;

    setProgress({
      state: 'preparing',
      progress: 0,
      uploadedBytes: 0,
      totalBytes: file.size,
      error: null,
      videoId: null,
    });

    try {
      // `File.type` est une chaîne libre fournie par le navigateur : elle est vide sur
      // certains systèmes et fantaisiste sur d'autres. On la valide contre la liste acceptée
      // avant l'envoi, plutôt que de laisser l'API renvoyer un 400 après coup.
      const contentType = detectContentType(file);
      if (!contentType) {
        setProgress((p) => ({
          ...p,
          state: 'error',
          error: `Format non pris en charge : ${file.name}. Formats acceptés : MP4, MOV, MKV.`,
        }));
        return null;
      }

      const init = await apiFetch<InitiateUploadResponse>('/videos/uploads', {
        method: 'POST',
        body: {
          fieldSessionId,
          filename: file.name,
          sizeBytes: file.size,
          contentType,
        } satisfies InitiateUploadInput,
      });

      setProgress((p) => ({ ...p, state: 'uploading', videoId: init.videoId }));

      // Analytique (ADR-0026) : `container` est le sous-type MIME (sans « video/ »), faible cardinalité.
      track('VIDEO_UPLOAD_STARTED', {
        container: contentType.split('/').pop() ?? 'unknown',
        size: sizeBucket(file.size),
      });

      const completed = await uploadParts(file, init, controller.signal, (uploadedBytes) => {
        setProgress((p) => ({
          ...p,
          uploadedBytes,
          progress: Math.min(1, uploadedBytes / file.size),
        }));
      });

      setProgress((p) => ({ ...p, state: 'completing' }));

      await apiFetch('/videos/uploads/complete', {
        method: 'POST',
        body: { videoId: init.videoId, parts: completed } satisfies CompleteUploadInput,
      });

      setProgress((p) => ({ ...p, state: 'done', progress: 1 }));
      track('VIDEO_UPLOAD_COMPLETED', { size: sizeBucket(file.size) });
      return init.videoId;
    } catch (error) {
      // Une annulation volontaire n'est pas une erreur : ne pas afficher de message d'échec.
      if (controller.signal.aborted) {
        setProgress((p) => ({ ...p, state: 'idle' }));
        return null;
      }
      setProgress((p) => ({
        ...p,
        state: 'error',
        error: error instanceof Error ? error.message : 'Échec de l’upload',
      }));
      // On ne journalise que l'échec, pas son message (texte libre — jamais dans l'analytique).
      track('VIDEO_UPLOAD_FAILED');
      return null;
    } finally {
      abortRef.current = null;
    }
  }, []);

  /** Reprend un upload interrompu après rechargement de page (PRD FR-7). */
  const resume = useCallback(async (file: File, videoId: string): Promise<string | null> => {
    const controller = new AbortController();
    abortRef.current = controller;

    setProgress({
      state: 'preparing',
      progress: 0,
      uploadedBytes: 0,
      totalBytes: file.size,
      error: null,
      videoId,
    });

    try {
      // Le serveur ne renvoie que les parts manquantes : celles déjà reçues par le stockage
      // sont exclues de la re-signature.
      const init = await apiFetch<InitiateUploadResponse>(`/videos/uploads/${videoId}/resume`, {
        method: 'POST',
      });

      const alreadyDone = Math.ceil(file.size / init.partSizeBytes) - init.parts.length;
      const alreadyDoneBytes = Math.min(file.size, alreadyDone * init.partSizeBytes);

      setProgress((p) => ({
        ...p,
        state: 'uploading',
        uploadedBytes: alreadyDoneBytes,
        progress: alreadyDoneBytes / file.size,
      }));

      const uploaded = await uploadParts(file, init, controller.signal, (uploadedBytes) => {
        const total = alreadyDoneBytes + uploadedBytes;
        setProgress((p) => ({
          ...p,
          uploadedBytes: total,
          progress: Math.min(1, total / file.size),
        }));
      });

      setProgress((p) => ({ ...p, state: 'completing' }));

      // La finalisation exige la liste complète des parts, y compris celles envoyées avant
      // l'interruption. Le serveur complète depuis le stockage ce que le client ignore.
      await apiFetch('/videos/uploads/complete', {
        method: 'POST',
        body: { videoId, parts: uploaded } satisfies CompleteUploadInput,
      });

      setProgress((p) => ({ ...p, state: 'done', progress: 1 }));
      return videoId;
    } catch (error) {
      setProgress((p) => ({
        ...p,
        state: 'error',
        error: error instanceof Error ? error.message : 'Échec de la reprise',
      }));
      return null;
    }
  }, []);

  const cancel = useCallback(async () => {
    abortRef.current?.abort();
    const videoId = progress.videoId;
    if (videoId) {
      // Libère les parts côté stockage, qui resteraient sinon facturées et occuperaient
      // de l'espace sans jamais être finalisées.
      await apiFetch(`/videos/uploads/${videoId}`, { method: 'DELETE' }).catch(() => {});
    }
  }, [progress.videoId]);

  return { progress, upload, resume, cancel };
}

/**
 * Envoie les parts avec un parallélisme borné, en rapportant l'avancement **en temps réel**.
 *
 * `MAX_CONCURRENT_UPLOADS` vaut 3 : au-delà, on sature le lien montant sans gagner de débit,
 * et le navigateur garde plusieurs tranches de 100 Mio en mémoire simultanément.
 *
 * `onProgress` reçoit le **total absolu** d'octets envoyés sur l'ensemble des parts confiées ici.
 * On suit l'avancement de chaque part en vol (octets XHR) plus les parts déjà terminées ; comme la
 * valeur est absolue (et non un cumul de deltas), un réessai qui repart de zéro **recalcule** juste
 * le total, sans double-comptage.
 */
async function uploadParts(
  file: File,
  init: InitiateUploadResponse,
  signal: AbortSignal,
  onProgress: (uploadedBytes: number) => void,
): Promise<Array<{ partNumber: number; etag: string }>> {
  const results: Array<{ partNumber: number; etag: string }> = [];
  const queue = [...init.parts];

  // Octets des parts terminées + octets en vol par part : la somme est le total absolu envoyé.
  let completedBytes = 0;
  const inFlight = new Map<number, number>();
  const report = () => {
    let sum = completedBytes;
    for (const loaded of inFlight.values()) sum += loaded;
    onProgress(sum);
  };

  const workers = Array.from(
    { length: Math.min(MAX_CONCURRENT_UPLOADS, queue.length) },
    async () => {
      for (;;) {
        const part = queue.shift();
        if (!part) return;

        const start = (part.partNumber - 1) * init.partSizeBytes;
        const chunk = file.slice(start, Math.min(start + init.partSizeBytes, file.size));

        const etag = await putPartWithRetry(part.url, chunk, signal, (loaded) => {
          inFlight.set(part.partNumber, loaded);
          report();
        });

        inFlight.delete(part.partNumber);
        completedBytes += chunk.size;
        results.push({ partNumber: part.partNumber, etag });
        report();
      }
    },
  );

  await Promise.all(workers);
  return results;
}

/**
 * Envoie une part, avec réessais et backoff exponentiel.
 *
 * C'est ce qui absorbe les micro-coupures : une part perdue coûte au pire une centaine de
 * mégaoctets à retransmettre, pas le fichier entier. À chaque tentative on repart de zéro pour cette
 * part (`onProgress(0)`), pour ne pas laisser l'avancement d'un essai raté gonfler le total.
 */
async function putPartWithRetry(
  url: string,
  chunk: Blob,
  signal: AbortSignal,
  onProgress: (loaded: number) => void,
): Promise<string> {
  let lastError: unknown;

  for (let attempt = 0; attempt < MAX_PART_RETRIES; attempt++) {
    try {
      return await putPart(url, chunk, signal, onProgress);
    } catch (error) {
      if (signal.aborted) throw error;
      lastError = error;
      onProgress(0);
      await delay(2 ** attempt * 1000);
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Échec d’envoi de la part');
}

/**
 * Envoie une part via **XMLHttpRequest** (et non `fetch`) : seul XHR expose la progression de
 * **téléversement** (`upload.onprogress`). Sans lui, la barre restait à 0 % pendant tout l'envoi
 * d'une part — pour un petit fichier tenant en une seule part, elle sautait de 0 à 100 % à la fin,
 * donnant l'impression d'un upload figé ou interminable.
 */
function putPart(
  url: string,
  chunk: Blob,
  signal: AbortSignal,
  onProgress: (loaded: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Upload annulé', 'AbortError'));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);

    const onAbort = () => xhr.abort();
    signal.addEventListener('abort', onAbort, { once: true });
    const cleanup = () => signal.removeEventListener('abort', onAbort);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded);
    };

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag');
        if (!etag) {
          // Cause la plus fréquente : CORS n'expose pas ETag. Sans lui, la finalisation S3
          // est impossible — voir la configuration CORS du bucket dans infra/.
          reject(new Error('En-tête ETag absent : vérifier ExposeHeaders dans la config CORS'));
          return;
        }
        resolve(etag.replace(/"/g, ''));
      } else {
        reject(new Error(`Le stockage a répondu ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      cleanup();
      reject(new Error('Erreur réseau pendant l’envoi de la part'));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException('Upload annulé', 'AbortError'));
    };

    xhr.send(chunk);
  });
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Détermine le type MIME à déclarer, en retombant sur l'extension quand le navigateur ne
 * fournit rien d'exploitable — logique partagée avec l'API (`canonicalVideoMime`). Le type réel
 * est de toute façon revérifié par `ffprobe` côté worker : ce contrôle sert le confort, pas la
 * sécurité. `null` (extension non reconnue) bloque l'upload côté client.
 */
function detectContentType(file: File): AcceptedVideoMimeType | null {
  return canonicalVideoMime(file.name, file.type);
}
